import type { Editor } from '@tiptap/core';
import { createPortal, useState, type Dispatch, type SetStateAction } from 'react';
import {
  RiArrowDownSLine,
  RiArrowGoBackFill,
  RiArrowGoForwardFill,
  RiBold,
  RiFontColor,
  RiFormula,
  RiItalic,
  RiLineHeight,
  RiUnderline,
} from 'react-icons/ri';

import ColorPicker from '../components/ColorPicker';
import DropDown, { DropDownItem } from '../components/dropdown';
import EquationEditorPopup from '../components/EquationEditorPopup';
import { blockTypeToBlockName, lineHeights } from './ToolbarContext';

export type EquationEditTarget = {
  latex: string;
  position: number;
};

type Props = {
  editor: Editor;
  type: 'none' | 'basic';
  showEquationEditorButton: boolean;
  equationEditorContainerElement?: HTMLElement;
  equationEditTarget: EquationEditTarget | null;
  equationEditorIsOpen: boolean;
  setEquationEditTarget: Dispatch<SetStateAction<EquationEditTarget | null>>;
  setEquationEditorIsOpen: Dispatch<SetStateAction<boolean>>;
};

function Divider() {
  return <div className="divider" />;
}

function activeClass(active: boolean) {
  return active ? 'active dropdown-item-active' : '';
}

function currentBlockType(editor: Editor): keyof typeof blockTypeToBlockName {
  if (editor.isActive('heading', { level: 1 })) return 'h1';
  if (editor.isActive('heading', { level: 2 })) return 'h2';
  if (editor.isActive('heading', { level: 3 })) return 'h3';
  return 'paragraph';
}

export default function ToolbarPlugin(props: Props) {
  if (props.showEquationEditorButton && !props.equationEditorContainerElement) {
    throw new Error('equationEditorContainerElement can not be null when showEquationEditorButton is true');
  }

  const [fontColorDropDownOpen, setFontColorDropDownOpen] = useState(false);
  const blockType = currentBlockType(props.editor);
  const fontColor = props.editor.getAttributes('textStyle').color || 'rgb(0, 0, 0)';
  const lineHeight = props.editor.getAttributes('paragraph').lineHeight || 'default';

  const closeEquationEditor = () => {
    props.setEquationEditorIsOpen(false);
    props.setEquationEditTarget(null);
  };

  return (
    <div className="toolbar">
      {props.type === 'basic' && <>
        <DropDown
          disabled={false}
          buttonClassName="toolbar-item spaced"
          buttonTitle="Heading"
          buttonLabel={blockTypeToBlockName[blockType]}
          buttonIcon={<RiArrowDownSLine />}
        >
          <DropDownItem className={`item wide ${activeClass(blockType === 'paragraph')}`} onClick={() => props.editor.chain().focus().setParagraph().run()}>
            <div className="icon-text-container"><span className="text">Paragraph</span></div>
          </DropDownItem>
          {([1, 2, 3] as const).map(level => <DropDownItem
            key={level}
            className={`item wide ${activeClass(blockType === `h${level}`)}`}
            onClick={() => props.editor.chain().focus().setHeading({ level }).run()}
          >
            <div className="icon-text-container"><span className={`text editor-heading-h${level}`}>Heading {level}</span></div>
          </DropDownItem>)}
        </DropDown>

        <DropDown disabled={false} buttonClassName="toolbar-item spaced" buttonTitle="Line Height" buttonIcon={<RiLineHeight />}>
          {lineHeights.map(option => <DropDownItem
            className={`item wide ${activeClass(lineHeight === option)}`}
            onClick={() => props.editor.chain().focus().updateAttributes('paragraph', { lineHeight: option === 'default' ? null : option }).run()}
            key={option}
          >
            <div className="icon-text-container"><span className="text">{option}</span></div>
          </DropDownItem>)}
        </DropDown>

        <button title="Bold" onClick={() => props.editor.chain().focus().toggleBold().run()} className={`toolbar-item spaced ${props.editor.isActive('bold') ? 'active' : ''}`} aria-label="Format Bold"><RiBold /></button>
        <button title="Italic" onClick={() => props.editor.chain().focus().toggleItalic().run()} className={`toolbar-item spaced ${props.editor.isActive('italic') ? 'active' : ''}`} aria-label="Format Italics"><RiItalic /></button>
        <button title="Underline" onClick={() => props.editor.chain().focus().toggleUnderline().run()} className={`toolbar-item spaced ${props.editor.isActive('underline') ? 'active' : ''}`} aria-label="Format Underline"><RiUnderline /></button>
        <DropDown
          disabled={false}
          stopCloseOnClickSelf={true}
          buttonTitle="Font Color"
          buttonClassName="toolbar-item spaced"
          buttonAriaLabel="Choose font color"
          isOpen={fontColorDropDownOpen}
          handleToggle={setFontColorDropDownOpen}
          buttonIcon={<RiFontColor color={fontColor} />}
        >
          <ColorPicker
            currentColor={fontColor}
            close={() => setFontColorDropDownOpen(false)}
            applyColor={color => {
              setFontColorDropDownOpen(false);
              if (color) {
                props.editor.chain().focus().setColor(color).run();
              } else {
                props.editor.chain().focus().unsetColor().run();
              }
            }}
          />
        </DropDown>
        <Divider />
      </>}

      <button title="Undo" disabled={!props.editor.can().undo()} onClick={() => props.editor.chain().focus().undo().run()} className="toolbar-item spaced" aria-label="Undo"><RiArrowGoBackFill /></button>
      <button title="Redo" disabled={!props.editor.can().redo()} onClick={() => props.editor.chain().focus().redo().run()} className="toolbar-item" aria-label="Redo"><RiArrowGoForwardFill /></button>

      {props.showEquationEditorButton && <button
        className="toolbar-item insert-equation"
        aria-label="Insert Equation"
        title="Insert Equation"
        onClick={() => props.setEquationEditorIsOpen(open => !open)}
      >
        <RiFormula /> <span className="text">Insert Equation</span>
      </button>}

      {props.equationEditorIsOpen && props.equationEditorContainerElement && createPortal(
        <EquationEditorPopup
          close={closeEquationEditor}
          initialLatex={props.equationEditTarget?.latex}
          writeEquation={(svg, latex) => {
            const target = props.equationEditTarget;
            if (target) {
              props.editor.chain().focus().command(({ tr }) => {
                tr.setNodeMarkup(target.position, undefined, { svg, latex });
                return true;
              }).run();
            } else {
              props.editor.chain().focus().insertContent({ type: 'equation', attrs: { svg, latex } }).run();
            }
            closeEquationEditor();
          }}
        />,
        props.equationEditorContainerElement,
      )}
    </div>
  );
}
